import { useMemo, useState } from 'react';
import type { MsrAspZeile } from './typen';
import { ART, GEWERK, HERKUNFT, z } from './gemeinsam';
import {
  FilterBar, FilterDrawer, type FilterDefinition, type FilterWerte,
} from '../../muster';

export interface DatenpunkteFilter {
  asp: string; gewerk: string; anlage: string; art: string; herkunft: string;
  anbindung: string; suche: string;
}

export const DATENPUNKTE_FILTER_LEER: DatenpunkteFilter = {
  asp: '', gewerk: '', anlage: '', art: '', herkunft: '', anbindung: '', suche: '',
};

/**
 * Die Filterleiste der Datenpunktliste (Reiter 4).
 *
 * Umzug U6c: außen unverändert (dieselben Props), innen auf `FilterBar` und
 * `FilterDrawer` der Musterbibliothek. Zwei Filter stehen in der Leiste, die
 * übrigen vier im Schubfach — sechs Auswahlfelder nebeneinander waren bei
 * 1440 Breite eine zweite Zeile ohne Ordnung (Auftrag Abschnitt 11).
 */
export function Filterleiste({
  filter, onChange, asps, gezeigt, gesamt,
}: {
  filter: DatenpunkteFilter;
  onChange: (f: DatenpunkteFilter) => void;
  asps: MsrAspZeile[];
  gezeigt: number;
  gesamt: number;
}) {
  const [schubfach, setSchubfach] = useState(false);

  const definitionen: FilterDefinition[] = useMemo(() => [
    {
      schluessel: 'asp', beschriftung: 'ASP', art: 'auswahl',
      optionen: asps.map((a) => ({ wert: a.id, beschriftung: a.kennung })),
    },
    {
      schluessel: 'gewerk', beschriftung: 'Gewerk', art: 'auswahl',
      optionen: Object.entries(GEWERK).map(([k, n]) => ({ wert: k, beschriftung: n })),
    },
    {
      schluessel: 'art', beschriftung: 'Datenpunktart', art: 'auswahl',
      optionen: Object.entries(ART).map(([k, n]) => ({ wert: k, beschriftung: n })),
    },
    {
      schluessel: 'herkunft', beschriftung: 'Herkunft', art: 'auswahl',
      optionen: Object.entries(HERKUNFT).map(([k, n]) => ({ wert: k, beschriftung: n })),
    },
    {
      schluessel: 'anbindung', beschriftung: 'Anbindung', art: 'auswahl',
      optionen: [
        { wert: 'klemme', beschriftung: 'Klemme' },
        { wert: 'bus', beschriftung: 'Bus' },
      ],
    },
    { schluessel: 'anlage', beschriftung: 'Anlage', art: 'text' },
  ], [asps]);

  const werte: FilterWerte = {
    asp: filter.asp, gewerk: filter.gewerk, art: filter.art,
    herkunft: filter.herkunft, anbindung: filter.anbindung, anlage: filter.anlage,
  };

  const uebernehmen = (neu: FilterWerte) => onChange({
    ...filter,
    asp: String(neu.asp ?? ''),
    gewerk: String(neu.gewerk ?? ''),
    art: String(neu.art ?? ''),
    herkunft: String(neu.herkunft ?? ''),
    anbindung: String(neu.anbindung ?? ''),
    anlage: String(neu.anlage ?? ''),
  });

  return (
    <>
      <FilterBar
        filter={definitionen}
        werte={werte}
        beiAenderung={uebernehmen}
        inLeiste={2}
        suche={filter.suche}
        beiSuche={(s) => onChange({ ...filter, suche: s })}
        suchePlatzhalter="AKS, Bezeichnung, Aggregat …"
        beiSchubfach={() => setSchubfach(true)}
      >
        <span className="schwach" style={{ fontSize: 12 }}>
          {z(gezeigt)} von {z(gesamt)}
        </span>
      </FilterBar>

      <FilterDrawer
        offen={schubfach}
        filter={definitionen}
        werte={werte}
        beiAenderung={uebernehmen}
        beiSchliessen={() => setSchubfach(false)}
        beiZuruecksetzen={() => onChange(DATENPUNKTE_FILTER_LEER)}
        trefferText={`${z(gesamt)} Datenpunkte`}
      />
    </>
  );
}
